import { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  showAppLoaderSelector,
  userIdSelector,
  isExternalIncompleteUserSelector,
  keycloakConfigSelector,
  keycloakInitStatusSelector,
  keycloakVerifyStatusSelector,
  messagesSelector
} from '../helpers/selectors';
import { popup_MobileAppSelector_SetVisibility } from '../actions/popup';
import { handleKeycloakLogin, handleKeycloakLogout } from '../actions/keycloak';
import { addErrorMsg } from '../utils/flashMessage/creator';
import { getMessage, getRouteCompanyId, isRMF_companyId } from '../utils/utils';
import { VERIFY_STATUS } from '../constants/generic';
import {
  brandHasKeycloak,
  canUseKeycloakLogin,
  markSentToKeycloakLogin,
  wasSentToKeycloakLogin,
  KEYCLOAK_STATUS
} from '../utils/keycloak';

/**
 * Temporary gate on the web registration (/register/*) routes while registration
 * is being aligned with the mobile Keycloak flow.
 *
 * - A logged-in member (finishing an incomplete profile or adding a company
 *   profile) keeps access — for them the creation flow acts as "complete profile".
 * - A Keycloak-authenticated user still without a member (isExternalIncompleteUser)
 *   keeps access — that path is what the align work will build on — and keeps it for
 *   the whole registration he starts here: the step that ends the flag is the step
 *   that creates the member, and the member arrives in the same dispatch.
 * - A visitor Keycloak has answered for, with no session, is sent to the Keycloak
 *   login page: registration now starts there, and he comes back to this same URL
 *   as a Keycloak user without a member, i.e. as the case above.
 * - Everyone else is turned away to the homepage. What he is told on the way there
 *   is the only thing that varies, and it is spelled out branch by branch below.
 */
export default function RegisterAccessGuard() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // The invite the visitor asked for, and the URL Keycloak will bring him back to
  // (createLoginUrl defaults its redirectUri to location.href). It is what the
  // one-shot marker is keyed by, so one abandoned login only spends that one invite.
  const { pathname } = useLocation();
  // The guard spans several routes and only some of them carry a company; this is
  // undefined on the chooser screens, which is exactly what the RMF check needs.
  const routeCompanyId = getRouteCompanyId(useParams());
  const messages = useSelector(messagesSelector);
  const userId = useSelector(userIdSelector);
  const isExternalIncompleteUser = useSelector(isExternalIncompleteUserSelector);
  const showAppLoader = useSelector(showAppLoaderSelector);
  const keycloakConfig = useSelector(keycloakConfigSelector);
  const keycloakInitStatus = useSelector(keycloakInitStatusSelector);
  const keycloakVerifyStatus = useSelector(keycloakVerifyStatusSelector);

  // The two terms hand over to each other inside one dispatch: the first step of the
  // registration creates the member, which ends isExternalIncompleteUser, and the
  // same answer puts that member into the user slice (reducers/user.js). Neither the
  // member's own /me nor anything else has to land in between for the visitor to keep
  // the screens he is standing on.
  const allowed = Boolean(userId || isExternalIncompleteUser);
  // "This mount has already asked for a login and is waiting for the answer" - the
  // stored marker cannot play that part, it is only written once the visitor has
  // really left, and the effect can re-run before that.
  const loginRequested = useRef(false);

  useEffect(() => {
    if (allowed) return;

    // Every refusal ends on the homepage. They differ only in what the visitor is
    // told on the way there - and several say nothing, either because he already
    // knows (a login he cancelled himself) or because something else has spoken
    // already (the store links popup, the flash the /me reducer raises).
    const goHome = () => navigate('/', { replace: true });
    const refuse = (messageId) => {
      addErrorMsg(getMessage(messages, messageId));
      goHome();
    };

    // Renault Mobility France has no web registration at all, so the store links are
    // the whole answer - and they have to come before any Keycloak decision, or the
    // visitor is walked through a full login only to be told, on the way back, to
    // install the app. navigateToCreationPage screens this for in-app navigation;
    // a typed URL - the only way into a guarded route from outside - bypasses it.
    if (isRMF_companyId(routeCompanyId)) {
      dispatch(popup_MobileAppSelector_SetVisibility(true, null, routeCompanyId));
      goHome();
      return;
    }

    // Wait for the Keycloak silent check-sso to settle before deciding the visitor
    // is anonymous, otherwise a hard refresh would block before the session loads.
    if (showAppLoader) return;

    // The loader going down only means the app stopped waiting: a failed kc.init()
    // ends in ssoDelayLoader, which hides it on a timeout, and at that instant whether
    // the visitor has a session is genuinely unknown. So wait for Keycloak's own
    // answer too - but only where one can still come, since a brand without Keycloak
    // never inits and would leave the visitor here forever.
    if (!brandHasKeycloak(keycloakConfig)) {
      refuse('error.register.unavailable');
      return;
    }

    if (keycloakInitStatus === null) return;

    // handleKeycloakLogout owns the exit as well as the session: the Keycloak round
    // trip comes back to the origin, and its local fallback pushes home itself.
    const endSession = () => dispatch(handleKeycloakLogout());

    // Keycloak found a session, yet nobody was let in. Only GET /me can say why, and
    // the reason decides whether that session may be torn down - the one thing here
    // that cannot be undone. It must not be: while /me is in flight, because `allowed`
    // is equally false then and a member about to be let in would be logged out
    // instead (showAppLoader covers that window today - App.verifyExternalLogin only
    // drops it around the same dispatch that answers - but a logout is not worth
    // betting on React's batching, so read the store's own record of the answer);
    // and when /me never answered at all, because a 500 or a dropped connection is
    // not a verdict on the visitor, and spending his session on one would cost him
    // far more than the bounce he came here for.
    const handleSessionWithoutAccess = () => {
      const OUTCOME_BY_VERIFY_STATUS = {
        // /me was never asked: kc.init() found a session while a logout was being
        // finalised (App.handleKeycloak's logoutPending branch). No answer is coming,
        // so there is no pending success to destroy - and ending that leftover session
        // is what the half-finished logout was after in the first place.
        [VERIFY_STATUS.IDLE]: endSession,
        // The answer is what decides; landing it re-runs this effect.
        [VERIFY_STATUS.PENDING]: () => {},
        // The backend refuses the account behind the session: not activated, blocked,
        // anonymized. The /me reducer has already flashed which of the three; ending
        // the session is what stops that same live session from walking him straight
        // back in here on every attempt.
        [VERIFY_STATUS.REFUSED]: endSession,
        // The backend never ruled on him - a 5xx, a timeout, a connection that
        // dropped. He keeps the session and is told to try again. The /me reducer
        // flashed the same technical error when the call failed; it is repeated here
        // because that was potentially minutes ago, on another page, and a bounce
        // with no reason given is the dead end this guard exists to remove.
        [VERIFY_STATUS.FAILED]: () => refuse('error.form.technical'),
        // /me did answer about the member - found (MEMBER), or 404 and therefore an
        // incomplete external user (NO_MEMBER). Both grant access, so reaching this
        // line means something dropped it afterwards, i.e. a logout; whatever Keycloak
        // session is still standing is the stale half of it.
        [VERIFY_STATUS.MEMBER]: endSession,
        [VERIFY_STATUS.NO_MEMBER]: endSession
      };

      OUTCOME_BY_VERIFY_STATUS[keycloakVerifyStatus]?.();
    };

    // Keycloak brings him back to this exact URL: createLoginUrl defaults its
    // redirectUri to location.href, and /creation/:id is inside this guard, so
    // the invite link he asked for is what he returns to.
    const startLogin = () => {
      // Once per invite only - a visitor who comes back to it still anonymous (login
      // cancelled, browser Back) would otherwise be bounced out again on arrival,
      // with no way back. Any other invite link in this tab is still free to try.
      // Silently: he cancelled it himself, and a flash on every Back press is noise.
      if (wasSentToKeycloakLogin(pathname)) {
        goHome();
        return;
      }

      // The brand has a Keycloak, but this client cannot build a login URL (init
      // never resolved its endpoints). Same dead end as a login that fails to start.
      if (!canUseKeycloakLogin(keycloakConfig)) {
        refuse('error.form.technical');
        return;
      }

      if (loginRequested.current) return;
      loginRequested.current = true;

      // The marker records that he was sent, not that we meant to send him: a login
      // we could not start must leave the next attempt free, and must land him on
      // the fallback instead of on a loader that never comes down.
      dispatch(handleKeycloakLogin(keycloakConfig)).then((sent) => {
        if (sent) markSentToKeycloakLogin(pathname);
        else refuse('error.form.technical');
      });
    };

    // Only Keycloak answering "no session" makes him anonymous. FAIL means it did not
    // answer at all - he may well have a live session, and re-authenticating him with
    // prompt: 'login' would be wrong; AUTH means he has one that led nowhere. Neither
    // is a login worth starting, so both are terminal here.
    const INIT_OUTCOME = {
      [KEYCLOAK_STATUS.FAIL]: () => refuse('error.form.technical'),
      [KEYCLOAK_STATUS.AUTH]: handleSessionWithoutAccess,
      [KEYCLOAK_STATUS.UNAUTH]: startLogin
    };

    INIT_OUTCOME[keycloakInitStatus]?.();
  }, [
    allowed,
    showAppLoader,
    keycloakConfig,
    keycloakInitStatus,
    keycloakVerifyStatus,
    routeCompanyId,
    messages,
    pathname
  ]);

  if (allowed) return <Outlet />;
  return null;
}
